import React from 'react';
import { AcademicCapIcon, UserGroupIcon, GlobeAsiaAustraliaIcon } from '@heroicons/react/24/solid';
import GlassyIcon from '../common/GlassyIcon';

const ShowcaseCard = ({ icon, title, description, children, color = 'green' }) => (
  <div 
    className="relative overflow-hidden rounded-2xl backdrop-blur-sm border-2 border-border p-4 sm:p-6 flex flex-col items-center text-center"
    style={{
      background: 'var(--color-background-secondary)',
    }}
  >
    {/* Subtle background pattern - adjusted for mobile */}
    <div className="absolute inset-0 opacity-15">
      <div
        className="absolute rounded-full top-0 right-0 translate-x-2 sm:translate-x-4 -translate-y-3 sm:-translate-y-6 bg-primary/80"
        style={{
          width: '48px',
          height: '48px',
        }}
      />
      <div
        className="absolute rounded-full bottom-0 left-0 -translate-x-2 sm:-translate-x-3 translate-y-2 sm:translate-y-4 bg-secondary/80"
        style={{
          width: '32px',
          height: '32px',
        }}
      />
    </div>

    <div className="relative z-10 flex flex-col items-center h-full">
      {/* Glassy Icon */}
      <div className="flex-shrink-0 mb-3 sm:mb-4">
        <GlassyIcon 
          icon={icon} 
          size="md"
          color={color}
        />
      </div>

      <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 drop-shadow-sm">{title}</h3>
      <p className="text-xs sm:text-sm text-content-secondary flex-grow leading-relaxed">{description}</p>
      
      <div 
        className="mt-3 sm:mt-4 w-full p-3 sm:p-4 rounded-xl border-2 border-border backdrop-blur-sm bg-background-primary/50"
      >
        {children}
      </div>
    </div>
  </div>
);

export const PremiumGlimpse = () => (
  <div className="mt-16 sm:mt-24 px-4 sm:px-0">
    <h2 className="text-2xl sm:text-3xl font-extrabold text-content-primary text-center">Learn Japanese Through Listening</h2>
    <p className="mt-4 text-lg sm:text-xl text-content-secondary max-w-5xl mx-auto text-center">
      Our platform helps you understand the building blocks of the language through comprehensive content and interactive learning.
    </p>
    <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
      <ShowcaseCard
        icon={AcademicCapIcon}
        title="Master Grammar Structures"
        description="Learn detailed breakdowns of grammar points and see how they're used in real sentences across all JLPT levels."
        color="green"
      >
        <p className="text-base sm:text-lg" lang="ja" style={{ color: 'var(--color-stat-learned)' }}>〜ことがある</p>
        <p className="text-xs text-content-secondary mt-1">Expressing &quot;have experience of...&quot;</p>
      </ShowcaseCard>
      
      <ShowcaseCard
        icon={UserGroupIcon}
        title="Understand Social Context"
        description="Go beyond literal translation. Discover the difference between casual, polite, and humble speech patterns."
        color="amber"
      >
        <p className="text-base sm:text-lg" lang="ja" style={{ color: 'var(--color-stat-reviewed)' }}>食べる？ vs. 召し上がりますか？</p>
        <p className="text-xs text-content-secondary mt-1">&quot;To eat&quot; (Casual vs. Respectful)</p>
      </ShowcaseCard>
      
      <ShowcaseCard
        icon={GlobeAsiaAustraliaIcon}
        title="Explore Cultural Context"
        description="Learn about Japanese traditions and cultural context behind sentences to better understand native speakers."
        color="violet"
      >
        <p className="text-base sm:text-lg" lang="ja" style={{ color: 'var(--color-stat-accuracy)' }}>雛祭り (ひなまつり)</p>
        <p className="text-xs text-content-secondary mt-1">The Japanese Doll Festival</p>
      </ShowcaseCard>
    </div>
  </div>
);